import { api } from '@goaltree/backend/convex/_generated/api'
import { FINISH_SHINE_MAX_CELLS } from '@goaltree/backend/convex/lib/dotFinishes'
import { useQuery } from 'convex/react'
import i18next from 'i18next'
import {
  type CSSProperties,
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { useFinishText } from '@/components/dot-finishes'
import { photoMark, useHabitPhotos, usePhotoPress } from '@/components/dot-photo'
import { shadeMark, shadeSummary, shadeTip, useCellTip, useShadeMap } from '@/components/dot-shade'
import { useDotMarks } from '@/components/habit-dots'
import { useBreather } from '@/hooks/use-breather'
import { useExpandStagger } from '@/hooks/use-expand-stagger'
import { CATEGORY_COLORS, useCategoryFill } from '@/lib/category-color'
import { breatherDayKeys, keyToDate, logicalDayStr, todayKey } from '@/lib/day'

const WEEKS = 53 // ~1 year of daily columns
const COMPACT_DAYS = 14 // recent daily window shown in the compact row
const COMPACT_CELL = 18 // target compact dot size; extra width shows more history
const COMPACT_WEEKS = 14 // recent weekly window — same dot count/size as the daily row
const FALLBACK_GRID_COLS = 24 // month columns before the container width is measured
// Every expanded grid uses this one square size so habits look the same whatever their age.
const GRID_CELL = 14
const DAILY_COL_PITCH = GRID_CELL + 3 // daily-full column: dot + 3px gap
const DAILY_MOUNT_BUFFER = 8 // columns mounted beyond the left viewport edge
const FIT_MIN_WEEKS = 8 // fit mode never shows fewer weeks than this
const FIT_MAX_WEEKS = 26 // habits older than this use the scrollable year grid

// Uncategorized habits follow the app's accent (primary) rather than a fixed
// green, so the dot grid matches the user's chosen accent theme.
const DEFAULT_COLOR = 'var(--primary)'
const NO_HABIT = { title: '' }

export function habitColor(category: string | undefined): string {
  return CATEGORY_COLORS[category?.toLowerCase() ?? ''] ?? DEFAULT_COLOR
}

function toLocalDateStr(d: Date): string {
  const tz = d.getTimezoneOffset()
  return new Date(d.getTime() - tz * 60_000).toISOString().slice(0, 10)
}

function addDays(d: Date, n: number): Date {
  const x = new Date(d)
  x.setDate(x.getDate() + n)
  return x
}

// ── Cadence-aware bucketing ────────────────────────────────────────────────
// Daily-ish habits (daily / everyOtherDay / weekday / weekend) track per-day;
// weekly habits track per-week; monthly per-month. Completions and the grid
// cells are bucketed by the same unit so a once-a-week habit shows a filled
// week-dot instead of six empty daily dots.

type CadenceMode = 'day' | 'week' | 'month'

function cadenceMode(period: string | null | undefined): CadenceMode {
  if (period === 'weekly') return 'week'
  if (period === 'monthly') return 'month'
  return 'day'
}

// Whether a habit of this cadence is scheduled on the given weekday (0=Sun).
// weekend habits skip Mon–Fri; weekday habits skip Sat/Sun; specific-days habits
// run on their days; everything else runs every day.
function isScheduledDay(
  dow: number,
  period: string | null | undefined,
  days?: readonly number[] | null,
): boolean {
  if (period === 'weekend') return dow === 0 || dow === 6
  if (period === 'weekday') return dow >= 1 && dow <= 5
  if (period === 'days' && days?.length) return days.includes(dow)
  return true
}

// Off-days (a weekday for a weekend habit, or vice versa) get a faint diagonal
// hatch so they read as "not scheduled", not "missed".
const OFF_DAY_STYLE = {
  backgroundImage:
    'repeating-linear-gradient(-45deg, transparent 0 1.5px, color-mix(in srgb, var(--muted-foreground) 30%, transparent) 1.5px 3px)',
} as const

// A breather day: a calm neutral fill — clearly not a missed day, not a hit.
// Days before the habit existed: an outline, so they never read as misses.
const PRE_START_CLASS = ' border border-dashed border-foreground/15'

const PAUSED_STYLE = {
  backgroundColor: 'color-mix(in srgb, var(--muted-foreground) 18%, transparent)',
} as const

/** The period-bucket key that a logical day ('YYYY-MM-DD') belongs to. */
function periodKeyOf(dayStr: string, mode: CadenceMode): string {
  if (mode === 'month') return dayStr.slice(0, 7) // YYYY-MM
  if (mode === 'week') {
    const d = new Date(`${dayStr}T00:00:00`)
    return toLocalDateStr(addDays(d, -d.getDay())) // Sunday-anchored week key
  }
  return dayStr
}

/** Start-of-current-period anchor date for "now". */
function periodAnchor(today: Date, mode: CadenceMode): Date {
  if (mode === 'month') return new Date(today.getFullYear(), today.getMonth(), 1)
  if (mode === 'week') return addDays(today, -today.getDay())
  return today
}

/** Step an anchor date back by `n` whole periods. */
function stepBack(anchor: Date, mode: CadenceMode, n: number): Date {
  if (mode === 'month') return new Date(anchor.getFullYear(), anchor.getMonth() - n, 1)
  if (mode === 'week') return addDays(anchor, -n * 7)
  return addDays(anchor, -n)
}

/** The set of completed period keys for a completion log, in the given cadence. */
function doneSet(completions: number[], mode: CadenceMode): Set<string> {
  return new Set(completions.map((ms) => periodKeyOf(logicalDayStr(ms), mode)))
}

// Whether a completion falls in the habit's current period (today / this week /
// this month), so a weekly habit done on Monday still reads as done on Friday.
export function inCurrentPeriod(ms: number, period?: string | null): boolean {
  const mode = cadenceMode(period)
  return periodKeyOf(logicalDayStr(ms), mode) === periodKeyOf(logicalDayStr(Date.now()), mode)
}

export function ctaPeriod(period?: string | null): 'Day' | 'Week' | 'Month' {
  const mode = cadenceMode(period)
  return mode === 'week' ? 'Week' : mode === 'month' ? 'Month' : 'Day'
}

// Consecutive-period streak from the completion log — cadence-aware, and matches
// the dot grid exactly. Counts back from the current period (or the previous one
// as a grace period, so "this period not done yet" doesn't zero the streak).
export function habitDayStreak(
  completions: number[],
  period?: string | null,
  days?: readonly number[] | null,
): number {
  if (!completions.length) return 0
  const mode = cadenceMode(period)
  const done = doneSet(completions, mode)
  const today = keyToDate(todayKey())
  const keyAt = (d: Date) => periodKeyOf(toLocalDateStr(d), mode)
  let cursor = periodAnchor(today, mode)
  if (!done.has(keyAt(cursor))) cursor = stepBack(cursor, mode, 1)
  let streak = 0
  for (let i = 0; i < 1000; i++) {
    const hit = done.has(keyAt(cursor))
    // Unscheduled days (weekends for a weekday habit) neither extend nor break it.
    if (!hit && mode === 'day' && !isScheduledDay(cursor.getDay(), period, days)) {
      cursor = stepBack(cursor, mode, 1)
      continue
    }
    if (!hit) break
    streak++
    cursor = stepBack(cursor, mode, 1)
  }
  return streak
}

export type HabitStats = { now: number; best: number; done: number; last30: number | null }

// Summary for the expanded history: current + best run, total periods done, and
// (daily cadences) the share of scheduled days done in the last 30 — clamped to
// days since the habit was created.
export function habitStats(
  completions: number[],
  period?: string | null,
  createdAt?: number,
  days?: readonly number[] | null,
): HabitStats {
  const mode = cadenceMode(period)
  const done = doneSet(completions, mode)
  const today = keyToDate(todayKey())
  const keyAt = (d: Date) => periodKeyOf(toLocalDateStr(d), mode)
  const earliest = Math.min(createdAt ?? Number.POSITIVE_INFINITY, ...completions)
  let best = 0
  let run = 0
  let cursor = periodAnchor(today, mode)
  // Today's period not done yet shouldn't break the run that ends yesterday.
  if (!done.has(keyAt(cursor))) cursor = stepBack(cursor, mode, 1)
  for (let i = 0; i < 800 && cursor.getTime() >= earliest - 86_400_000 * 31; i++) {
    const hit = done.has(keyAt(cursor))
    // Unscheduled days (e.g. weekends on a weekday habit) neither extend nor break a run.
    if (hit || mode !== 'day' || isScheduledDay(cursor.getDay(), period, days)) {
      run = hit ? run + 1 : 0
      best = Math.max(best, run)
    }
    cursor = stepBack(cursor, mode, 1)
  }
  let last30: number | null = null
  if (mode === 'day') {
    let scheduled = 0
    let hit = 0
    for (let i = 0; i < 30; i++) {
      const d = addDays(today, -i)
      if (createdAt && d.getTime() < new Date(createdAt).setHours(0, 0, 0, 0)) break
      if (!isScheduledDay(d.getDay(), period, days)) continue
      scheduled++
      if (done.has(keyAt(d))) hit++
    }
    last30 = scheduled ? Math.round((hit / scheduled) * 100) : 0
  }
  return { now: habitDayStreak(completions, period, days), best, done: done.size, last30 }
}

// Build the last `count` period cells (oldest → newest), each flagged done.
function buildPeriodCells(done: Set<string>, mode: CadenceMode, count: number) {
  const today = keyToDate(todayKey())
  const anchor = periodAnchor(today, mode)
  return Array.from({ length: count }, (_, i) => {
    const date = stepBack(anchor, mode, count - 1 - i)
    return {
      key: periodKeyOf(toLocalDateStr(date), mode),
      date,
      done: done.has(periodKeyOf(toLocalDateStr(date), mode)),
    }
  })
}

// A finish's glint in the full grid: down each column, then on to the next column.
const gridGlint = (col: number, row: number) => col + row / 4

export const HabitDotGrid = memo(function HabitDotGrid({
  completions,
  color: rawColor,
  period,
  recursDays,
  compact = false,
  animate = true,
  sweepOnMountMs,
  createdAt,
  habitId,
}: {
  completions: number[]
  color: string
  period?: string | null
  recursDays?: readonly number[] | null
  compact?: boolean
  animate?: boolean
  // Sweep the dots in order once on mount, after this many ms.
  sweepOnMountMs?: number
  // Full daily grid leaves days before the habit existed blank.
  createdAt?: number
  // Looks up the days its goal was frozen, shown as rest days like a breather.
  habitId?: string
}) {
  const mode = cadenceMode(period)
  const color = useCategoryFill()(rawColor)
  const habits = useQuery(api.nodes.getHabits, habitId ? {} : 'skip')
  const entry = habitId ? habits?.find((h) => h._id === habitId) : undefined
  // With an earned finish, empty and today cells take the metal's tone, not the
  // category colour, so the grid reads as one material.
  const finishText = useFinishText()
  const habitVar = {
    '--habit': entry?.finish ? finishText(entry.finish) : color,
  } as CSSProperties
  const stag = useExpandStagger(compact, sweepOnMountMs)

  // Entrance uses an opacity *transition* (not a keyframe animation): keyframes
  // restart whenever the element is remounted or reparented — which virtualized
  // list updates do — causing the grid to animate more than once. A transition
  // only fires on the 0→1 opacity change and is unaffected by reparenting.
  const [shown, setShown] = useState(!animate)
  useEffect(() => {
    if (!animate) {
      setShown(true)
      return
    }
    // Two rAFs on purpose: the first lets the browser paint the initial opacity:0,
    // the second flips to opacity:1 so the CSS transition reliably fires from a
    // painted start. A single rAF can commit 0→1 in the same frame — the browser
    // never paints 0, so the transition is skipped or half-plays and sticks.
    let raf2 = 0
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => setShown(true))
    })
    return () => {
      cancelAnimationFrame(raf1)
      cancelAnimationFrame(raf2)
    }
  }, [animate])

  // Daily-full grid lazily mounts only the columns near the horizontal scroll
  // window (+ a left buffer); off-window columns stay as fixed-width empty
  // placeholders. This cuts the expand from mounting a full year of day-cells
  // (53×7) to just the visible window, so expanding many habits at once stays
  // cheap. mountFrom only ever decreases (mount-on-reveal, never unmount) to
  // avoid re-mount churn while scrolling.
  const scrollRef = useRef<HTMLDivElement>(null)
  const [dailyMountFrom, setDailyMountFrom] = useState(WEEKS)
  const recomputeMount = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    const firstVisible = Math.floor(el.scrollLeft / DAILY_COL_PITCH)
    const next = Math.max(0, firstVisible - DAILY_MOUNT_BUFFER)
    setDailyMountFrom((prev) => Math.min(prev, next))
  }, [])

  // Anchor to the most recent entry (right edge) then seed the mount window.
  // Re-runs on toggle so switching compact ↔ full re-anchors. useLayoutEffect
  // (not useEffect) so the visible columns mount before paint — no empty flash.
  useLayoutEffect(() => {
    const el = scrollRef.current
    if (!el) return
    el.scrollLeft = el.scrollWidth
    recomputeMount()
  }, [compact, recomputeMount])

  // Weekly/monthly detail spans the container width: measure it and show exactly
  // as many month columns (extending into the past) as fit at the daily dot pitch.
  const gridRef = useRef<HTMLDivElement>(null)
  const [gridCols, setGridCols] = useState(FALLBACK_GRID_COLS)
  useLayoutEffect(() => {
    const el = gridRef.current
    if (!el) return
    const measure = () => {
      const pitch = 14 // 10px dot + ~4px gap
      setGridCols(Math.max(6, Math.floor((el.clientWidth + 4) / pitch)))
    }
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [mode, compact])

  // Compact strip shows as many recent days as fit at its cell size.
  const compactRef = useRef<HTMLDivElement>(null)
  const [compactWidth, setCompactWidth] = useState(0)
  useLayoutEffect(() => {
    const el = compactRef.current
    if (!el) return
    const measure = () => setCompactWidth(el.clientWidth)
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [mode, compact])

  // Young-habit grid pads with pre-start weeks until it spans the card width.
  const fitRef = useRef<HTMLDivElement>(null)
  const [fitWidth, setFitWidth] = useState(0)
  useLayoutEffect(() => {
    const el = fitRef.current
    if (!el) return
    const measure = () => setFitWidth(el.clientWidth)
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [mode, compact])

  const dotStyle = (done: boolean, i: number, step: number) => ({
    ...habitVar,
    ...(done ? { backgroundColor: color } : {}),
    opacity: shown ? 1 : 0,
    ...(animate ? { transition: 'opacity 260ms ease', transitionDelay: `${i * step}ms` } : {}),
  })

  // Days spent on a breather render as a neutral marker, not a missed day.
  const { from, until } = useBreather()
  const frozenDays = entry?.frozenDays
  const marks = useDotMarks(entry?.dot, entry ?? NO_HABIT, entry?.finish)
  // Custom dot styles: a done cell's glyph, and an empty ring (or missed-day
  // image) for a scheduled day that was missed.
  // `i` = position in a row short enough to animate a finish's sweep.
  // Photo days show their photo; days with something logged are shaded by amount.
  const photos = useHabitPhotos(habitId, entry?.dot?.kind === 'photo')
  const shades = useShadeMap(entry)
  const mark = (done: boolean, missed: boolean, size: number, i?: number, ds?: string) => {
    if (ds) {
      const shot = photoMark(photos?.get(ds), size)
      if (shot) return shot
      const s = shades?.get(ds)
      if (s && s.credit > 0) return shadeMark(marks.done(size, i), s)
    }
    return done ? marks.done(size, i) : missed ? marks.missed(size) : null
  }
  const canAddPhoto = useCallback(
    (ds: string) => {
      const from = toLocalDateStr(addDays(keyToDate(todayKey()), -3))
      return ds >= from && completions.some((ms) => logicalDayStr(ms) === ds)
    },
    [completions],
  )
  const photoPress = usePhotoPress(
    entry?.dot?.kind === 'photo'
      ? { id: entry._id, title: entry.title, category: entry.category, photoAsk: entry.photoAsk }
      : null,
    photos,
    canAddPhoto,
  )
  const tipper = useCellTip(photoPress.onDay)
  const interactive = !!shades || !!photos
  const pausedDays = useMemo(
    () => new Set([...breatherDayKeys(from, until), ...(frozenDays ?? [])]),
    [from, until, frozenDays],
  )
  // A past period with no check-in since the habit began is missed (ring, or the
  // missed-day image); the current period is still open.
  const periodMissed = (done: boolean, start: Date, end: Date, current: boolean) =>
    !done &&
    !current &&
    (!createdAt || end.getTime() >= new Date(createdAt).setHours(0, 0, 0, 0)) &&
    start.getTime() <= Date.now()

  // ── Weekly / monthly ─────────────────────────────────────────────────────
  // Weekly compact: a full-width row of equal-share week-dots (same size as the
  // daily row) with a month-initial label under the first week of each month.
  if (mode === 'week' && compact) {
    const cells = buildPeriodCells(doneSet(completions, 'week'), 'week', COMPACT_WEEKS)
    const mLabel = new Intl.DateTimeFormat(i18next.language, { month: 'narrow' })
    let prevMonth = -1
    const labels = cells.map((cell) => {
      const m = cell.date.getMonth()
      const show = m !== prevMonth
      prevMonth = m
      return show ? mLabel.format(cell.date) : ''
    })
    return (
      <div className="flex flex-col gap-1">
        <div className="flex gap-[3px]">
          {cells.map((cell, i) => {
            const m = mark(
              cell.done,
              periodMissed(cell.done, cell.date, addDays(cell.date, 6), i === cells.length - 1),
              COMPACT_CELL,
              cells.length - i <= FINISH_SHINE_MAX_CELLS ? i : undefined,
            )
            return (
              <span
                key={cell.key}
                title={cell.key}
                className={`aspect-square min-w-0 flex-1 overflow-hidden rounded-[3px]${cell.done ? '' : i === cells.length - 1 ? ' bg-[color-mix(in_srgb,var(--habit)_28%,transparent)]' : ' bg-[color-mix(in_srgb,var(--habit)_16%,transparent)]'}${i === cells.length - 1 ? ' ring-[1.5px] ring-inset ring-[var(--habit)]' : ''}`}
                style={{ ...dotStyle(cell.done, i, 14), ...m?.style }}
              >
                {m?.children}
              </span>
            )
          })}
        </div>
        <div className="flex gap-1">
          {labels.map((l, i) => (
            <span
              key={i}
              className="min-w-0 flex-1 text-[8px] leading-none text-muted-foreground/50"
            >
              {l}
            </span>
          ))}
        </div>
      </div>
    )
  }

  // Monthly (compact + full look the same): a row of small month-dots — the same
  // fixed size as the daily grid — spanning the container width, each labelled
  // with its month initial (J, F, M…).
  if (mode === 'month') {
    const cells = buildPeriodCells(doneSet(completions, 'month'), 'month', gridCols)
    const monthMark = (cell: (typeof cells)[number], i: number) =>
      mark(
        cell.done,
        periodMissed(
          cell.done,
          cell.date,
          new Date(cell.date.getFullYear(), cell.date.getMonth() + 1, 0),
          i === cells.length - 1,
        ),
        10,
      )
    const mLabel = new Intl.DateTimeFormat(i18next.language, { month: 'narrow' })
    return (
      <div ref={gridRef} className="w-full">
        <div className="flex justify-between">
          {cells.map((cell, i) => (
            <div key={cell.key} className="flex flex-col items-center gap-1">
              <span
                title={cell.key}
                className={`h-2.5 w-2.5 shrink-0 overflow-hidden rounded-[3px]${cell.done ? '' : ' bg-[color-mix(in_srgb,var(--habit)_16%,transparent)]'}`}
                style={{ ...dotStyle(cell.done, i, 10), ...monthMark(cell, i)?.style }}
              >
                {monthMark(cell, i)?.children}
              </span>
              <span className="text-[8px] leading-none text-muted-foreground/50">
                {mLabel.format(cell.date)}
              </span>
            </div>
          ))}
        </div>
      </div>
    )
  }

  // Weekly-full: a mini calendar — one flex column per month (filling the
  // container width), 4 rows for the weeks of that month, with a month-initial
  // label (J, F, M…) under each column.
  if (mode === 'week') {
    const doneCells = new Set<string>()
    for (const ms of completions) {
      const dayStr = logicalDayStr(ms)
      const dom = Number(dayStr.slice(8, 10))
      doneCells.add(`${dayStr.slice(0, 7)}:${Math.min(3, Math.floor((dom - 1) / 7))}`)
    }
    const now = new Date()
    now.setHours(0, 0, 0, 0)
    const ymOf = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
    const curYm = ymOf(now)
    const curWeekRow = Math.min(3, Math.floor((now.getDate() - 1) / 7))
    const initialFmt = new Intl.DateTimeFormat(i18next.language, { month: 'narrow' })
    const monthCols = Array.from({ length: gridCols }, (_, i) => {
      const d = new Date(now.getFullYear(), now.getMonth() - (gridCols - 1 - i), 1)
      const ym = ymOf(d)
      return {
        ym,
        label: initialFmt.format(d),
        weeks: Array.from({ length: 4 }, (_, r) => {
          const start = new Date(d.getFullYear(), d.getMonth(), r * 7 + 1)
          const done = doneCells.has(`${ym}:${r}`)
          return {
            done,
            future: ym === curYm && r > curWeekRow,
            missed: periodMissed(
              done,
              start,
              r === 3 ? new Date(d.getFullYear(), d.getMonth() + 1, 0) : addDays(start, 6),
              ym === curYm && r === curWeekRow,
            ),
          }
        }),
      }
    })
    return (
      <div ref={gridRef} className="w-full">
        <div className="flex justify-between">
          {monthCols.map((col, ci) => (
            <div key={col.ym} className="flex flex-col items-center gap-1">
              <div className="flex flex-col gap-[3px]">
                {col.weeks.map((wk, r) => (
                  <span
                    key={r}
                    className={`h-2.5 w-2.5 shrink-0 overflow-hidden rounded-[3px]${stag.cls}${
                      wk.done
                        ? ''
                        : wk.future
                          ? ' bg-transparent'
                          : ' bg-[color-mix(in_srgb,var(--habit)_16%,transparent)]'
                    }`}
                    style={
                      wk.future
                        ? {}
                        : {
                            ...habitVar,
                            ...(wk.done ? { backgroundColor: color } : {}),
                            opacity: shown ? 1 : 0,
                            ...(animate
                              ? {
                                  transition: 'opacity 260ms ease',
                                  transitionDelay: `${(ci * 4 + r) * 6}ms`,
                                }
                              : {}),
                            ...stag.at(ci * 4 + r, gridCols * 4),
                            ...mark(wk.done, wk.missed, 10)?.style,
                          }
                    }
                  >
                    {!wk.future && mark(wk.done, wk.missed, 10)?.children}
                  </span>
                ))}
              </div>
              <span className="text-[8px] leading-none text-muted-foreground/50">{col.label}</span>
            </div>
          ))}
        </div>
      </div>
    )
  }

  // ── Daily: the classic GitHub-style grid (unchanged) ─────────────────────
  const set = new Set(completions.map((ms) => logicalDayStr(ms)))
  const today = keyToDate(todayKey())
  const todayStr = toLocalDateStr(today)
  // Fill whole weeks: end on the Saturday of the current week, start WEEKS back.
  const gridEnd = addDays(today, 6 - today.getDay())
  const gridStart = addDays(gridEnd, -(WEEKS * 7 - 1))
  const createdStr = createdAt ? toLocalDateStr(new Date(createdAt)) : ''
  const cellFmt = new Intl.DateTimeFormat(i18next.language, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  })
  const cellTitle = (ds: string, done: boolean) =>
    `${cellFmt.format(new Date(`${ds}T12:00:00`))}${done ? ` · ${i18next.t('goals.done')}` : ''}`
  const tipOf = (ds: string) =>
    entry?.measure
      ? shadeTip(i18next.t.bind(i18next), i18next.language, ds, shades?.get(ds), entry.measure)
      : null
  const cellProps = (ds: string, done: boolean) => ({
    title: tipOf(ds) ?? cellTitle(ds, done),
    ...(interactive && { 'data-day': ds, 'data-tip': tipOf(ds) ?? undefined }),
  })
  const summary =
    shades && entry?.measure
      ? shadeSummary(i18next.t.bind(i18next), i18next.language, shades, entry.measure, todayStr)
      : null

  // Compact: a single horizontal row of the last 30 days that fills the width.
  if (compact) {
    // As many fixed-size dots as fit (today always last and visible).
    const count = compactWidth
      ? Math.max(7, Math.floor((compactWidth + 3) / (COMPACT_CELL + 3)))
      : COMPACT_DAYS
    const days = Array.from({ length: count }, (_, i) => {
      const d = addDays(today, -(count - 1 - i))
      const ds = toLocalDateStr(d)
      const done = set.has(ds)
      return {
        ds,
        done,
        pre: !done && ds < createdStr,
        off: !isScheduledDay(d.getDay(), period, recursDays),
      }
    })
    return (
      // justify-between spreads the fixed-size dots edge to edge; only the gaps flex.
      <div
        ref={compactRef}
        className="flex min-w-0 flex-1 justify-between gap-[3px] overflow-hidden"
        onClick={interactive ? tipper.press : undefined}
      >
        {tipper.bubble}
        {photoPress.node}
        {days.map((cell, i) => {
          const paused = !cell.done && !cell.pre && pausedDays.has(cell.ds)
          const off = cell.off && !cell.done && !cell.pre && !paused
          const m = mark(
            cell.done,
            !cell.done && !cell.pre && !off && !paused && cell.ds !== todayStr,
            COMPACT_CELL,
            days.length - i <= FINISH_SHINE_MAX_CELLS ? i : undefined,
            cell.ds,
          )
          return (
            <span
              key={cell.ds}
              {...cellProps(cell.ds, cell.done)}
              className={`shrink-0 overflow-hidden rounded-[3px]${cell.pre ? PRE_START_CLASS : cell.done || off || paused ? '' : cell.ds === todayStr ? ' bg-[color-mix(in_srgb,var(--habit)_28%,transparent)]' : ' bg-[color-mix(in_srgb,var(--habit)_16%,transparent)]'}${cell.ds === todayStr ? ' ring-[1.5px] ring-inset ring-[var(--habit)]' : ''}`}
              // Fixed size so dots match across rows; a narrower row just fits fewer.
              style={{
                width: COMPACT_CELL,
                height: COMPACT_CELL,
                ...dotStyle(cell.done, i, 14),
                ...(paused ? PAUSED_STYLE : off ? OFF_DAY_STYLE : {}),
                ...m?.style,
              }}
            >
              {m?.children}
            </span>
          )
        })}
      </div>
    )
  }

  // Young habits (≤ FIT_MAX_WEEKS old) show only the weeks since creation, with
  // cells stretched to fill the row — big, readable squares instead of a mostly
  // blank year. Older habits keep the scrollable full-year grid below.
  const fitWeeks = createdAt
    ? Math.max(
        FIT_MIN_WEEKS,
        Math.ceil(
          (gridEnd.getTime() -
            addDays(new Date(createdAt), -new Date(createdAt).getDay()).getTime()) /
            (7 * 86_400_000),
        ),
      )
    : Number.POSITIVE_INFINITY
  if (fitWeeks <= FIT_MAX_WEEKS) {
    // 18px = weekday label column + gap; the extra columns are all pre-start days.
    const widthWeeks = fitWidth ? Math.floor((fitWidth - 18 + 3) / DAILY_COL_PITCH) : 0
    const weeks = Math.min(WEEKS, Math.max(fitWeeks, widthWeeks))
    const fitStart = addDays(gridEnd, -(weeks * 7 - 1))
    const cells = Array.from({ length: weeks * 7 }, (_, i) => {
      const ds = toLocalDateStr(addDays(fitStart, i))
      const done = set.has(ds)
      return {
        ds,
        done,
        future: ds > todayStr,
        pre: !done && ds < createdStr,
        off: !isScheduledDay(i % 7, period, recursDays),
      }
    })
    const hasPre = cells.some((c) => c.pre)
    const monthFmt = new Intl.DateTimeFormat(i18next.language, { month: 'short' })
    const monthLabels = Array.from({ length: weeks }, (_, c) => {
      const d = addDays(fitStart, c * 7)
      const prev = c > 0 ? addDays(fitStart, (c - 1) * 7) : null
      return !prev || prev.getMonth() !== d.getMonth() ? monthFmt.format(d) : ''
    })
    const wdFmt = new Intl.DateTimeFormat(i18next.language, { weekday: 'narrow' })
    const cols = { gridTemplateColumns: `repeat(${weeks}, ${GRID_CELL}px)` }
    return (
      <div ref={fitRef} onClick={interactive ? tipper.press : undefined}>
        {tipper.bubble}
        {photoPress.node}
        <div className="flex gap-1.5">
          <div className="flex shrink-0 flex-col">
            <div className="h-4" />
            <div className="grid grid-rows-7 gap-[3px]">
              {Array.from({ length: 7 }, (_, r) => (
                <span
                  key={r}
                  className="flex w-3 items-center justify-end text-[9px] leading-none text-muted-foreground/60"
                  style={{ height: GRID_CELL }}
                >
                  {r === 1 || r === 3 || r === 5 ? wdFmt.format(new Date(2025, 0, 5 + r)) : ''}
                </span>
              ))}
            </div>
          </div>
          <div
            className="min-w-0 flex-1 overflow-x-auto overflow-y-hidden [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            ref={(el) => {
              // Open on the latest weeks when a long history overflows.
              if (el) el.scrollLeft = el.scrollWidth
            }}
          >
            <div
              className="mb-1 grid h-3 gap-[3px] text-[9px] leading-none text-muted-foreground/60"
              style={cols}
            >
              {monthLabels.map((m, c) => (
                <span key={c} className="overflow-visible whitespace-nowrap">
                  {m}
                </span>
              ))}
            </div>
            <div className="grid grid-flow-col grid-rows-7 gap-[3px]" style={cols}>
              {cells.map((cell, i) => {
                const paused = !cell.done && !cell.future && !cell.pre && pausedDays.has(cell.ds)
                const off = cell.off && !cell.done && !cell.future && !cell.pre && !paused
                const m = cell.future
                  ? null
                  : mark(
                      cell.done,
                      !cell.done && !cell.pre && !off && !paused && cell.ds !== todayStr,
                      GRID_CELL,
                      gridGlint(Math.floor(i / 7), i % 7),
                      cell.ds,
                    )
                return (
                  <span
                    key={cell.ds}
                    {...(cell.future || cell.pre ? {} : cellProps(cell.ds, cell.done))}
                    className={`size-[14px] overflow-hidden rounded-[3px]${stag.cls}${
                      cell.done
                        ? ''
                        : cell.pre
                          ? PRE_START_CLASS
                          : cell.future || off || paused
                            ? ' bg-transparent'
                            : cell.ds === todayStr
                              ? ' bg-[color-mix(in_srgb,var(--habit)_28%,transparent)]'
                              : ' bg-[color-mix(in_srgb,var(--habit)_12%,transparent)]'
                    }${cell.ds === todayStr ? ' ring-[1.5px] ring-inset ring-[var(--habit)]' : ''}`}
                    style={
                      cell.future
                        ? {}
                        : {
                            ...dotStyle(cell.done, i, 4),
                            ...stag.at(i, cells.length),
                            ...(paused ? PAUSED_STYLE : off ? OFF_DAY_STYLE : {}),
                            ...m?.style,
                          }
                    }
                  >
                    {m?.children}
                  </span>
                )
              })}
            </div>
          </div>
        </div>
        {hasPre && (
          <div className="mt-1.5 ml-[18px] flex items-center gap-1.5 text-[9px] leading-none text-muted-foreground/60">
            <span className={`h-2.5 w-2.5 rounded-[3px]${PRE_START_CLASS}`} />
            {i18next.t('inbox.legendBeforeStart')}
          </div>
        )}
        {summary && <p className="mt-1.5 text-[11px] text-muted-foreground">{summary}</p>}
      </div>
    )
  }

  const columns = Array.from({ length: WEEKS }, (_, c) =>
    Array.from({ length: 7 }, (_, r) => {
      const ds = toLocalDateStr(addDays(gridStart, c * 7 + r))
      // r is the weekday (0=Sun); off = not scheduled for this cadence.
      return {
        ds,
        done: set.has(ds),
        future: ds > todayStr || (ds < createdStr && !set.has(ds)),
        off: !isScheduledDay(r, period, recursDays),
      }
    }),
  )

  const monthFmt = new Intl.DateTimeFormat(i18next.language, { month: 'short' })
  const months: string[] = []
  let prevMonth = -1
  for (let c = 0; c < WEEKS; c++) {
    const d = addDays(gridStart, c * 7)
    if (d.getMonth() !== prevMonth) {
      prevMonth = d.getMonth()
      months.push(monthFmt.format(d))
    }
  }

  // Narrow weekday labels for Mon/Wed/Fri (rows 1/3/5; row 0 = Sunday).
  const wdFmt = new Intl.DateTimeFormat(i18next.language, { weekday: 'narrow' })
  const weekdayLabels = Array.from({ length: 7 }, (_, r) =>
    r === 1 || r === 3 || r === 5 ? wdFmt.format(new Date(2025, 0, 5 + r)) : '',
  )

  return (
    <div>
      <div className="flex gap-1" onClick={interactive ? tipper.press : undefined}>
        {tipper.bubble}
        {photoPress.node}
        {/* Weekday labels */}
        <div className="flex shrink-0 flex-col">
          <div className="h-3.5" />
          <div className="flex flex-col gap-[3px]">
            {weekdayLabels.map((wl, r) => (
              <span
                key={r}
                className="flex w-3 items-center justify-end text-[8px] leading-none text-muted-foreground/50"
                style={{ height: GRID_CELL }}
              >
                {wl}
              </span>
            ))}
          </div>
        </div>

        {/* Month labels + dot grid. overflow-y-hidden stops the grid nudging
          vertically; with no vertical scroll of its own, a vertical drag falls
          through to the page (default touch-action) while horizontal drags
          scroll the grid. */}
        <div
          ref={scrollRef}
          className="overflow-x-auto overflow-y-hidden"
          onScroll={recomputeMount}
        >
          <div className="w-max">
            <div className="mb-1 flex justify-between text-[8px] leading-none text-muted-foreground/50">
              {months.map((m, i) => (
                <span key={i} className="whitespace-nowrap">
                  {m}
                </span>
              ))}
            </div>
            {/* Reserve the exact grid height (7 cells + 6×3px gaps) so the row is a
              stable height from the first render — before any columns lazily mount
              or content-visibility resolves. This lets AnimatedHeight measure the
              correct expanded target in one pass, so the expand tween never
              re-targets mid-flight (the residual "clunky" stutter). */}
            <div className="flex gap-[3px]" style={{ minHeight: GRID_CELL * 7 + 18 }}>
              {columns.map((col, ci) => (
                // Fixed-width columns so off-window ones hold their place (stable
                // scroll width + right anchor) without mounting any cells; content-
                // visibility additionally skips paint of off-screen columns. Only
                // columns at/after the mount window render their 7 day-cells.
                <div
                  key={ci}
                  className="flex flex-col gap-[3px]"
                  style={{
                    width: GRID_CELL,
                    contentVisibility: 'auto',
                    containIntrinsicSize: `${GRID_CELL}px ${GRID_CELL * 7 + 18}px`,
                  }}
                >
                  {ci >= dailyMountFrom &&
                    col.map((cell, r) => {
                      const paused = !cell.done && !cell.future && pausedDays.has(cell.ds)
                      const off = cell.off && !cell.done && !cell.future && !paused
                      const m = cell.future
                        ? null
                        : mark(
                            cell.done,
                            !cell.done && !off && !paused && cell.ds !== todayStr,
                            GRID_CELL,
                            gridGlint(ci, r),
                            cell.ds,
                          )
                      return (
                        <span
                          key={cell.ds}
                          {...(cell.future ? {} : cellProps(cell.ds, cell.done))}
                          // Future days render invisible so the grid stops at today;
                          // off-days show a hatch, breather days a neutral fill —
                          // neither reads as "missed".
                          className={`size-[14px] shrink-0 overflow-hidden rounded-[3px]${stag.cls}${
                            cell.done
                              ? ''
                              : cell.future || off || paused
                                ? ' bg-transparent'
                                : cell.ds === todayStr
                                  ? ' bg-[color-mix(in_srgb,var(--habit)_28%,transparent)]'
                                  : ' bg-[color-mix(in_srgb,var(--habit)_12%,transparent)]'
                          }${cell.ds === todayStr ? ' ring-[1.5px] ring-inset ring-[var(--habit)]' : ''}`}
                          style={
                            cell.future
                              ? {}
                              : {
                                  ...habitVar,
                                  ...(cell.done
                                    ? { backgroundColor: color }
                                    : paused
                                      ? PAUSED_STYLE
                                      : off
                                        ? OFF_DAY_STYLE
                                        : {}),
                                  opacity: shown ? 1 : 0,
                                  ...(animate
                                    ? {
                                        transition: 'opacity 260ms ease',
                                        transitionDelay: `${(ci + r) * 6}ms`,
                                      }
                                    : {}),
                                  ...stag.at(
                                    (ci - dailyMountFrom) * 7 + r,
                                    (WEEKS - dailyMountFrom) * 7,
                                  ),
                                  ...m?.style,
                                }
                          }
                        >
                          {m?.children}
                        </span>
                      )
                    })}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      {summary && <p className="mt-1.5 ml-4 text-[11px] text-muted-foreground">{summary}</p>}
    </div>
  )
})
